/**
 * @fileoverview Hook powering the AI Operations dashboard — owns the overview
 *   snapshot, the selected time window, async state, and refresh.
 * @module system-web/hooks/useAiOperations
 *
 * Responsibilities:
 * - Load the AI Operations overview on mount and whenever the window changes.
 * - Expose loading / error state, the selected window, and a manual refresh.
 *
 * Dependencies:
 * - aiOperationsService (inherently-online monitoring API wrapper).
 *
 * Architecture:
 * - Local-first EXCEPTION (see aiOperationsService): monitoring data is live and
 *   non-persisted. The hook owns ALL async state + window selection; the page
 *   renders from the hook and never calls the API directly.
 *
 * Notes:
 * - The previous snapshot is retained while a refresh is in flight so the UI does
 *   not flash empty on manual refresh / window change.
 *
 * @hook useAiOperations
 * Returns: overview snapshot + window controls + loading/error + refresh.
 * Side Effects: fetches the overview on mount and on window change.
 * Offline Behavior: monitoring is inherently online — surfaces an error string
 *   when the network/API is unavailable (no cached fallback by design).
 */

import { useCallback, useEffect, useState } from 'react';
import { getAiOperationsOverview, type AiOperationsOverview } from '../services/aiOperationsService';

export type AiWindowHours = 6 | 24 | 72 | 168;

export const AI_WINDOW_OPTIONS: { label: string; value: AiWindowHours }[] = [
  { label: '6h', value: 6 },
  { label: '24h', value: 24 },
  { label: '3d', value: 72 },
  { label: '7d', value: 168 },
];

function errMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Unknown error';
}

export function useAiOperations(initialWindow: AiWindowHours = 24) {
  const [windowHours, setWindowHours] = useState<AiWindowHours>(initialWindow);
  const [overview, setOverview] = useState<AiOperationsOverview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAiOperationsOverview(windowHours)
      .then((data) => {
        if (!cancelled) setOverview(data);
      })
      .catch((err) => {
        if (!cancelled) setError(errMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [windowHours, reloadKey]);

  return {
    overview,
    loading,
    error,
    windowHours,
    setWindowHours,
    refresh,
  };
}
